import React, { useState, useMemo, useRef } from "react";
import { format, isSameDay, parseISO, addDays, startOfISOWeek } from "date-fns";
import { sv } from "date-fns/locale";
import { CardBody } from "reactstrap";
import { formatDate } from "../../Utilities/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import APLPresenceCheckTable from "./APLPresenceCheckTable";

const NUMBER_OF_WEEKS_TO_SHOW = 4;
const DATE_FORMAT_DAY_NAME = "EEE";
const DATE_FORMAT_DAY_NUMBER = "d";
const DATE_FORMAT_DAY_NUMBER_OF_WEEK = "i";
const DATE_FORMAT_WEEK_NUMBER = "I";
const DATE_FORMAT_YEAR = "yyyy";

function getWeekDays(week, periodFrom, periodTo, todayDate) {
  let monday;
  if (week.attendanceForStudents.length > 0) {
    monday = startOfISOWeek(parseISO(week.attendanceForStudents[0].day));
  } else {
    const jan4 = new Date(week.year, 0, 4);
    monday = addDays(startOfISOWeek(jan4), (week.weekNumber - 1) * 7);
  }

  const days = [];
  for (let d = 0; d < 5; d++) {
    const day = addDays(monday, d);
    if (day >= periodFrom && day <= periodTo && day <= todayDate) {
      days.push(day);
    }
  }
  return days;
}

const APLPresenceWeeksView = React.memo(function APLPresenceWeeksView({
  attendanceByWeek,
  studentId,
  objId,
  level,
  activePeriodId,
}) {
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
  const [selectedDayData, setSelectedDayData] = useState(null);

  const today = useRef(new Date());

  const weekIntervals = useMemo(() => {
    const tempWeekIntervals = [];
    const currentWeekNumber = format(today.current, DATE_FORMAT_WEEK_NUMBER, {
      locale: sv,
    });
    const currentYear = format(today.current, DATE_FORMAT_YEAR, {
      locale: sv,
    });

    for (
      let i = 0;
      i < attendanceByWeek.data.weeks.length;
      i += NUMBER_OF_WEEKS_TO_SHOW
    ) {
      const chunk = attendanceByWeek.data.weeks.slice(
        i,
        i + NUMBER_OF_WEEKS_TO_SHOW
      );
      tempWeekIntervals.push(chunk);
    }
    let currentWeekChunk = 0;
    tempWeekIntervals.forEach((interval, i) => {
      if (
        interval.some(
          (weeks) =>
            weeks.weekNumber === Number(currentWeekNumber) &&
            weeks.year === Number(currentYear)
        )
      ) {
        currentWeekChunk = i;
      }
    });

    setCurrentWeekIndex(currentWeekChunk);
    return tempWeekIntervals;
  }, [attendanceByWeek.data.weeks]);

  const cells = useMemo(() => {
    const result = [];
    const statusClassMap = {
      HadNoRegistrations: "no-registrations",
      WasPresent: "present",
      HadAbsence: "absent",
    };

    const currentWeeks = weekIntervals[currentWeekIndex];
    if (!currentWeeks) return result;

    const periodFrom = parseISO(attendanceByWeek.data.periodFrom);
    const periodTo = parseISO(attendanceByWeek.data.periodTo);

    // Build lookup from sparse attendance data for this student
    const attendanceLookup = new Map();
    currentWeeks.forEach((week) => {
      week.attendanceForStudents
        .filter((record) => record.studentId === studentId)
        .forEach((record) => {
          attendanceLookup.set(record.day.substring(0, 10), record);
        });
    });

    // Generate cells for all weekdays in each week
    currentWeeks.forEach((week) => {
      const weekDays = getWeekDays(week, periodFrom, periodTo, today.current);

      const monthsInWeek = [
        ...new Set(weekDays.map((d) => format(d, "MMM", { locale: sv }))),
      ];
      const formattedMonth = monthsInWeek.join("/");

      weekDays.forEach((day) => {
        const dateKey = format(day, "yyyy-MM-dd");
        const item = attendanceLookup.get(dateKey) || null;

        const formattedDayName = format(day, DATE_FORMAT_DAY_NAME, {
          locale: sv,
        });
        const formattedDayNumber = format(day, DATE_FORMAT_DAY_NUMBER, {
          locale: sv,
        });
        const formattedDayNumberOfWeek = format(
          day,
          DATE_FORMAT_DAY_NUMBER_OF_WEEK,
          { locale: sv }
        );
        const formattedWeekNumber = format(day, DATE_FORMAT_WEEK_NUMBER, {
          locale: sv,
        });

        const statusClass = item
          ? statusClassMap[item.status] || ""
          : statusClassMap["HadNoRegistrations"];

        result.push({
          key: studentId + "" + dateKey,
          parsedDay: day,
          dateKey,
          formattedDayName,
          formattedDayNumber,
          formattedDayNumberOfWeek,
          formattedWeekNumber,
          formattedMonth,
          statusClass,
          item,
        });
      });
    });

    return result;
  }, [weekIntervals, currentWeekIndex, studentId, attendanceByWeek.data.periodFrom, attendanceByWeek.data.periodTo]);

  const renderDaysAndCells = () => {
    return (
      <div className="apl-presence__weeks-grid">
        {currentWeekIndex > 0 && (
          <div
            className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_left p-1"
            onClick={() => changeWeekHandle("prev")}
          >
            <FontAwesomeIcon icon="chevron-left" />
          </div>
        )}
        <div className="apl-presence__week-cells">
          {cells.map((cell) => {
            const isToday = isSameDay(cell.parsedDay, today.current);
            const isSelected =
              selectedDayData?.day?.substring(0, 10) === cell.dateKey;
            const isWeekStart = cell.formattedDayNumberOfWeek === "1";
            return (
              <div
                className={
                  "apl-presence__week-day-col" +
                  (isWeekStart ? " apl-presence__week-day-col--week-start" : "")
                }
                key={cell.key}
              >
                {isWeekStart && (
                  <div className="apl-presence__week-label">
                    v.{cell.formattedWeekNumber}{" "}
                    <span style={{ fontWeight: 400 }}>
                      ({cell.formattedMonth})
                    </span>
                  </div>
                )}
                <div
                  className={
                    "apl-presence__week-cell " +
                    cell.statusClass +
                    (isToday ? " apl-presence__week-cell--today" : "") +
                    (isSelected ? " apl-presence__week-cell--selected" : "")
                  }
                  onClick={() => onDateClickHandle(cell.parsedDay, cell.item)}
                >
                  <span className="apl-presence__week-day-init">
                    {cell.formattedDayName.charAt(0)}
                  </span>
                  <span className="apl-presence__week-day-num">
                    {cell.formattedDayNumber}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {currentWeekIndex + 1 < weekIntervals.length && (
          <div
            className="calendar_change_dateInterval_chevron calendar_change_dateInterval_chevron_right p-1"
            onClick={() => changeWeekHandle("next")}
          >
            <FontAwesomeIcon icon="chevron-right" />
          </div>
        )}
      </div>
    );
  };

  const onDateClickHandle = (day, attendanceRecord) => {
    if (attendanceRecord) {
      setSelectedDayData(attendanceRecord);
    } else {
      setSelectedDayData({
        day: format(day, "yyyy-MM-dd"),
        attendanceThisDay: { points: [] },
        absencesThisDay: [],
        status: "HadNoRegistrations",
      });
    }
  };

  const changeWeekHandle = (btnType) => {
    if (btnType === "prev") {
      setCurrentWeekIndex(currentWeekIndex - 1);
    } else if (btnType === "next") {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  };

  const renderDetails = () => {
    if (selectedDayData?.day) {
      return (
        <div className="apl-presence__day-detail">
          <div className="apl-presence__day-detail-head">
            <h3 className="apl-presence__day-detail-title">
              {formatDate(selectedDayData?.day, "do MMMM yyyy")}
            </h3>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setSelectedDayData(null)}
            />
          </div>
          <CardBody>
            <APLPresenceCheckTable
              presenceData={selectedDayData}
              absence={selectedDayData.absencesThisDay}
              periodId={activePeriodId}
              studentId={studentId}
              objId={objId}
              level={level}
            />
          </CardBody>
        </div>
      );
    } else {
      return null;
    }
  };
  if (weekIntervals.length >= 1) {
    return (
      <div className="calendar px-4">
        {renderDaysAndCells()}
        {selectedDayData !== null && renderDetails()}
      </div>
    );
  } else {
    return null;
  }
});

export default APLPresenceWeeksView;
